import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import CmsImage from '../components/CmsImage';
import { fetchLocalizedCollection } from '../lib/cms';

export default function ProductsLandingPage() {
  const [categories, setCategories] = useState([]);
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Standard fallback categories matching PMB's requested product portfolio structure
  const defaultCategories = [
    {
      title: 'Coilanlæg og følgeudstyr',
      desc: 'Komplette industrielle coil-linjer, båndfremføring, haspler og retteværker til automatiseret stanse- og presseproduktion.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/epknx.jpg',
      link: '/coilanlaeg',
      group: null,
    },
    {
      title: 'Fiberlaser',
      desc: 'Højpræcisions fiberlaserskæring fra MVD med avanceret resonator, høj skærehastighed og lavt energiforbrug i alle pladetykkelser.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/bearbejdning-1.webp',
      link: '/fiberlaser',
      group: 'Laser',
      tag: 'Laser',
    },
    {
      title: 'Rørlaser',
      desc: 'Avancerede 3D-rørlasere til præcisionsskæring i rør og åbne/lukkede profiler med maksimal fleksibilitet og effektivitet.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/bearbejdning.png',
      link: '/roerlaser',
      group: 'Laser',
      tag: 'Laser',
    },
    {
      title: 'Slibe- og afgratemaskiner',
      desc: 'Industrielle afgrater- og slibemaskiner til professionel finish, afgratning og kantafronding af udskårne metalemner.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/maskiner.jpg',
      link: '/slibe-og-afgratemaskiner',
      group: null,
    },
    {
      title: 'Pladeklipning',
      desc: 'Guillotinesakse til klipning af stål- og metalplader med CNC-styret spalte- og snitvinkel for rene snit uden deformation.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/mvd-ishear-guillotine-shear.png',
      link: '/pladeklipning',
      group: null,
    },
    {
      title: 'Panelbukker',
      desc: 'Automatiske og fleksible panelbukkere til hurtig og præcis bukning af kassetter, paneler og tyndpladeemner.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/mvd-e-series.webp',
      link: '/panelbukker',
      tag: 'Nyhed',
      group: null,
    },
    {
      title: 'Hydraulisk presse',
      desc: 'Kraftfulde hydrauliske produktionspresser fra Hidroliksan med præcis trykregulering, ideelle til dybtræk og formgivning.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/presser.png',
      link: '/hydraulisk-presse',
      group: 'Presse',
      tag: 'Presse',
    },
    {
      title: 'Kantpresse',
      desc: 'MVD iBend servo-elektriske og hydrauliske kantpressere med præcis bukkevinkel, CNC-bombering og lavt energiforbrug.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/mvd-e-series.webp',
      link: '/kantpresse',
      group: 'Presse',
      tag: 'Presse',
    },
    {
      title: 'Mekanisk presse',
      desc: 'Robuste mekaniske excenterpresser og højhastighedspresser til stansning og kontinuerlig serieproduktion.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/maskiner.jpg',
      link: '/mekanisk-presse',
      group: 'Presse',
      tag: 'Presse',
    },
    {
      title: 'Styringer og software',
      desc: 'Intelligente CNC-maskinstyringer, retrofit-opgraderinger og produktionssoftware til optimeret oppetid og sikkerhed.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/fremforing-hero.webp',
      link: '/styringer-og-software',
      group: null,
    },
    {
      title: 'Brugte maskiner',
      desc: 'Totalrenoverede og gennemtestede brugte presser og maskiner. Høj kvalitet med garanti og fuld driftssikkerhed.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/brugte-maskiner.jpg',
      link: '/brugte-maskiner',
      tag: 'Klar til levering',
      group: null,
    },
    {
      title: 'Reservedele',
      desc: 'Omfattende reservedelslager til PMB-presser, excenterpresser, smøresystemer og sliddele direkte fra Borup.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/reservedele.png',
      link: '/reservedele',
      group: null,
    },
  ];

  useEffect(() => {
    Promise.all([
      fetchLocalizedCollection('ydelser_produkter', 'da'),
      fetchLocalizedCollection('sider', 'da'),
    ])
      .then(([cmsItems, pages]) => {
        // Page CMS data
        const pMatch = (pages || []).find((p) => {
          const slug = typeof p?.slug === 'object' ? p.slug?.da : p?.slug;
          return slug === 'produkter';
        });
        if (pMatch) setPageData(pMatch);

        // Filter and map CMS categories
        if (cmsItems && cmsItems.length > 0) {
          const mapped = cmsItems
            .filter((it) => {
              const slug = typeof it?.slug === 'object' ? it.slug?.da : it?.slug;
              // Exclude service, keep products/machinery
              return slug !== 'service-montage';
            })
            .map((cat) => {
              const slug = typeof cat?.slug === 'object' ? cat.slug?.da : cat?.slug;
              const title = cat.titel === 'Laser' ? 'Laser & Fiberlaser' : cat.titel;
              const link =
                cat.cta_link ||
                (slug === 'kantpresser' ? '/kantbukker' : `/${slug}`);
              const img = Array.isArray(cat.billede) && cat.billede.length > 0 ? cat.billede[0] : null;

              return {
                title,
                desc: cat.kort_beskrivelse,
                img,
                link,
                slug,
              };
            });

          if (mapped.length > 0) {
            // Merge with default list for missing images or missing items
            const merged = defaultCategories.map((def) => {
              const cmsMatch = mapped.find(
                (m) =>
                  m.link === def.link ||
                  (def.link === '/kantpresse' && (m.link === '/kantpresser' || m.link === '/kantbukker' || m.slug === 'kantpresser')) ||
                  (def.link === '/pladeklipning' && (m.link === '/saks' || m.slug === 'saks')) ||
                  (def.link === '/slibe-og-afgratemaskiner' && (m.link === '/slibemaskiner' || m.slug === 'slibemaskiner')) ||
                  (def.link === '/coilanlaeg' && (m.link === '/coilanlaeg' || m.slug === 'coilanlaeg')) ||
                  m.title.toLowerCase() === def.title.toLowerCase()
              );
              if (cmsMatch) {
                return {
                  ...def,
                  title: def.title, // Keep clean requested category title
                  desc: cmsMatch.desc || def.desc,
                  img: cmsMatch.img || def.img,
                  link: def.link,
                };
              }
              return def;
            });

            setCategories(merged);
          } else {
            setCategories(defaultCategories);
          }
        } else {
          setCategories(defaultCategories);
        }
        setLoading(false);
      })
      .catch(() => {
        setCategories(defaultCategories);
        setLoading(false);
      });
  }, []);

  const displayList = categories.length > 0 ? categories : defaultCategories;

  return (
    <>
      <Helmet>
        <title>
          {pageData?.seo_titel ||
            'Produkter – Maskiner & Udstyr til Metalbearbejdning | PMB'}
        </title>
        <meta
          name="description"
          content={
            pageData?.meta_beskrivelse ||
            'Se PMBs samlede produktprogram inden for metal- og pladebearbejdning: kantbukkere, presser, laserskæring, guillotinesakse, følgeudstyr og brugte maskiner.'
          }
        />
        <meta
          property="og:title"
          content={pageData?.seo_titel || 'Produkter – Maskiner & Udstyr til Metalbearbejdning | PMB'}
        />
        <meta
          property="og:description"
          content="Meget mere end kun excenterpressere. Vi tilbyder ALT indenfor metal- og pladebearbejdning."
        />
      </Helmet>

      {/* Breadcrumb */}
      <div className="bg-stone-100 border-b border-stone-200 py-3 px-4 sm:px-6 lg:px-8 text-xs text-stone-600">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Link to="/" className="hover:text-[#006368] transition">
            Forside
          </Link>
          <span className="text-stone-400">/</span>
          <span className="text-stone-900 font-semibold">Produkter</span>
        </div>
      </div>

      {/* Hero / Intro Sektion */}
      <section className="py-16 sm:py-24 bg-stone-50 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-5">
            <span className="text-xs font-bold uppercase tracking-widest text-[#006368] inline-block">
              JERES TOTALLEVERANDØR
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold text-stone-900 tracking-tight leading-tight">
              Meget mere end kun excenterpressere
            </h1>
            <p className="text-base sm:text-lg text-stone-700 leading-relaxed pt-2">
              Vi har i mange år været kendt for vores excenterpressere som vi stadig er meget stolte af – men PMB er meget mere end kun excenterpressere. Vi tilbyder ALT indenfor metal- og pladebearbejdning og udvider hele tiden vores produktsortiment til at matche vores kunders skiftende behov.
            </p>

            <div className="pt-4 flex flex-wrap gap-4">
              <a
                href="#kategorier"
                className="px-6 py-3 rounded bg-[#006368] hover:bg-[#004d51] text-white font-semibold text-sm tracking-wide transition shadow-sm flex items-center gap-2"
              >
                <span>Udforsk produktkategorier</span>
                <i className="fa-solid fa-arrow-down text-xs" />
              </a>
              <Link
                to="/kontakt"
                className="px-6 py-3 rounded bg-white hover:bg-stone-100 text-stone-800 border border-stone-300 font-semibold text-sm tracking-wide transition flex items-center gap-2"
              >
                <span>Få rådgivning af en specialist</span>
                <i className="fa-solid fa-arrow-right text-xs" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Produktkategorier Grid Sektion */}
      <section id="kategorier" className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-14">
            <span className="text-xs font-bold uppercase tracking-widest text-[#006368]">
              KOMPLET MASKINPROGRAM
            </span>
            <h2 className="text-2xl sm:text-4xl font-bold text-stone-900 tracking-tight mt-2">
              Vores mest populære produktkategorier
            </h2>
            <p className="mt-3 text-stone-600 max-w-2xl text-sm sm:text-base leading-relaxed">
              Vælg en kategori nedenfor for at se maskinmodeller, tekniske specifikationer og muligheder for kundetilpasning.
            </p>
          </div>

          {/* Grid af produktkategorier */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayList.map((cat, idx) => (
              <Link
                key={idx}
                to={cat.link}
                className="group flex flex-col bg-stone-50 rounded-lg border border-stone-200 overflow-hidden hover:border-[#006368] hover:shadow-lg transition-all duration-200"
              >
                <div className="relative h-60 w-full overflow-hidden bg-white p-6 flex items-center justify-center border-b border-stone-200">
                  {cat.tag && (
                    <span className="absolute top-3 right-3 bg-[#006368]/10 text-[#006368] text-[11px] font-bold px-2.5 py-1 rounded">
                      {cat.tag}
                    </span>
                  )}
                  {cat.img ? (
                    <CmsImage
                      src={cat.img}
                      alt={cat.title}
                      className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-300"
                      width={500}
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center text-stone-400 p-4 text-center">
                      <i className="fa-solid fa-industry text-3xl mb-2 text-[#006368]/40" />
                      <span className="text-xs font-semibold text-stone-500">{cat.title}</span>
                    </div>
                  )}
                </div>

                <div className="p-6 flex-1 flex flex-col justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-stone-900 group-hover:text-[#006368] transition-colors">
                      {cat.title}
                    </h3>
                    <p className="mt-2.5 text-sm text-stone-600 leading-relaxed">
                      {cat.desc}
                    </p>
                  </div>

                  <div className="mt-6 pt-4 border-t border-stone-200/80 flex items-center justify-between text-sm font-semibold text-[#006368] group-hover:text-[#004f53]">
                    <span>Se program & modeller</span>
                    <i className="fa-solid fa-arrow-right text-xs group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Bundbanner for personlig rådgivning */}
          <div className="mt-16 p-8 sm:p-12 rounded-xl bg-[#121619] text-white flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="space-y-2 text-center lg:text-left">
              <span className="text-xs font-bold uppercase tracking-widest text-[#0d8086]">
                Specielle ønsker eller kundetilpassede anlæg?
              </span>
              <h3 className="text-2xl sm:text-3xl font-bold">
                Vi rådgiver og tilpasser løsningen præcist til jeres behov
              </h3>
              <p className="text-stone-300 text-sm max-w-2xl">
                Har du brug for en specifik maskine, integration af styringer eller en komplet nøglefærdig produktionslinje? Vores teknikere sidder klar til at hjælpe.
              </p>
            </div>
            <div className="shrink-0 flex flex-col sm:flex-row gap-3">
              <a
                href="tel:57526262"
                className="px-6 py-3.5 rounded bg-white/10 hover:bg-white/20 text-white font-semibold text-sm border border-white/20 transition flex items-center justify-center gap-2"
              >
                <i className="fa-solid fa-phone text-xs" />
                <span>+45 57 52 62 62</span>
              </a>
              <Link
                to="/kontakt"
                className="px-6 py-3.5 rounded bg-[#006368] hover:bg-[#004d51] text-white font-bold text-sm tracking-wide transition shadow-lg flex items-center justify-center gap-2"
              >
                <span>Kontakt os</span>
                <i className="fa-solid fa-arrow-right text-xs" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
